// Copyright (c) StrangeBee 2023

import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { classList, convertToObject, handleNotificationError, useLocalMessage } from '@Helpers/';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { ActionOutput, ObservableOutput } from 'thehive-sdk';
import { AnalysisObservableTable } from './AnalysisObservableTable/AnalysisObservableTable';
import { useGenericQuery } from '@Queries/genericQueries';
import TemplateShim from '@Molecules/TemplateShim/TemplateShim';
import { computeEventList } from '@Organisms/AnalyzerTemplates/eventList';
import client from '@Client';
import { ObservableEventList } from '@Organisms/Observable/eventList';
import { THButton } from '@Molecules/Inputs/THButton/THButton';

import './AnalysisQuickview.scss';
import { useHistory } from 'react-router-dom';

interface AnalysisDataProps {
    observable?: ObservableOutput;
    data?: ActionOutput;
    importObservable?: boolean;
    eventList?: ObservableEventList;
    hasPermissions?: boolean;
}

const AnalysisData = ({
    data,
    importObservable,
    observable,
    eventList,
    hasPermissions = false,
}: AnalysisDataProps): ReactElement | null => {
    const local = useLocalMessage();
    const [template, setTemplate] = useState<string>('');
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const analyzerEventList = computeEventList({
        controller: client.AnalyzerTemplateController,
        onError: handleNotificationError,
    });

    const { extraData, analyzerName = '' } = data || {};
    const { report: reportValue } = extraData || {};
    const report = convertToObject(reportValue);
    const { artifacts = [], full, success } = report || {};

    const { data: dataTemplate, refetch: refetchDataTemplate } = useGenericQuery({
        fetchCallback: () => analyzerEventList.onGet(analyzerName),
        key: [`get-analyzer-template-${analyzerName}`],
        fetchOnStart: false,
        manual: true,
    });

    useEffect(() => {
        if (data && !dataTemplate) {
            refetchDataTemplate();
        }
    }, [data, dataTemplate, refetchDataTemplate]);

    const onClickHandler = useCallback(() => setIsOpen(!isOpen), [isOpen]);

    useEffect(() => {
        if (dataTemplate?.length) {
            const rightTemplate = dataTemplate?.find(({ analyzerId }) => analyzerId === analyzerName);
            if (rightTemplate) {
                setTemplate(rightTemplate.content);
            }
            setIsOpen(!Boolean(rightTemplate?.content));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [analyzerName, dataTemplate]);

    if (!data) {
        return null;
    }

    return (
        <div className="analysis-data">
            {!!artifacts?.length && observable && (
                <AnalysisObservableTable
                    hasPermissions={hasPermissions}
                    data={observable}
                    observables={artifacts}
                    analyzerName={analyzerName}
                    eventList={eventList}
                    importObservable={importObservable}
                />
            )}

            {template && (
                <>
                    <THButton type="link" className="analysis-data__button" onClick={onClickHandler}>
                        {local(isOpen ? 'analyzer.action.hide-raw' : 'analyzer.action.show-raw')}
                    </THButton>
                    {!isOpen ? (
                        <TemplateShim
                            template={template}
                            content={full || report}
                            success={success}
                            artifact={observable}
                        />
                    ) : null}
                </>
            )}
            <pre className={classList('analysis-data__raw', { show: isOpen || !template })}>
                <code>{JSON.stringify(full || report, null, 2)}</code>
            </pre>
        </div>
    );
};

interface AnalysisQuickviewProps {
    data?: ActionOutput;
    observable?: ObservableOutput;
    importObservable?: boolean;
    triggerElem?: ReactElement;
    onDrawerOpen?: () => void;
    eventList?: ObservableEventList;
    hasPermissions?: boolean;
    defaultVisible?: boolean;
}

export const AnalysisQuickview = ({
    data,
    observable,
    importObservable,
    triggerElem,
    onDrawerOpen,
    eventList,
    hasPermissions = false,
    defaultVisible,
}: AnalysisQuickviewProps): ReactElement | null => {
    const i = useLocalMessage();
    const history = useHistory();

    return (
        <THDrawer
            title={i('analyzer.quickview.drawer.title')}
            width="60vw"
            className="drawer-analysis-quickview"
            maskClosable
            destroyOnClose
            onChange={(visible): void => {
                if (visible) {
                    onDrawerOpen?.();
                }
            }}
            triggerElem={triggerElem}
            defaultVisible={defaultVisible}
            onClose={() => {
                const reportEnd = `/reports/${data?._id}`;
                const path = history.location.pathname;
                if (path.endsWith(reportEnd)) {
                    history.push(path.replace(reportEnd, ''));
                }
            }}
        >
            <AnalysisData
                data={data}
                hasPermissions={hasPermissions}
                importObservable={importObservable}
                observable={observable}
                eventList={eventList}
            />
        </THDrawer>
    );
};
